import { type ReactNode, type ComponentProps } from 'react';
import clsx from 'clsx';
// eslint-disable-next-line no-restricted-imports
import { Callout as RadixCallout } from '@radix-ui/themes';

import { IconButton } from 'components/common/IconButton';
import { AlertTriangleIcon, InfoIcon, CheckCircleIcon, CloseIcon } from 'components/common/Icons';

export type CalloutStatus = 'success' | 'error' | 'info' | 'warning';
type CalloutColor = 'sky' | 'red' | 'green' | 'amber';

type CalloutProps = ComponentProps<typeof RadixCallout.Root> & {
  status: CalloutStatus;
  className?: string;
  onClose?: () => void;
  children: ReactNode;
  customCloseButton?: ReactNode;
};

const COLOR_MAP: Record<CalloutStatus, CalloutColor> = {
  success: 'green',
  error: 'red',
  info: 'sky',
  warning: 'amber',
};

const CalloutIcon = ({ status, className }: { status: CalloutStatus; className?: string }): ReactNode => {
  switch (status) {
    case 'info':
      return <InfoIcon className={className} />;
    case 'error':
      return <AlertTriangleIcon className={className} />;
    case 'success':
      return <CheckCircleIcon className={className} />;
    case 'warning':
      return <AlertTriangleIcon className={className} />;
    default: {
      const _exhaustiveCheck: never = status;
      throw new Error(`Unknown status: ${_exhaustiveCheck}`);
    }
  }
};

export const Callout = ({ status, className, size, onClose, children, customCloseButton, ...props }: CalloutProps) => {
  const role = status === 'error' ? 'alert' : undefined;

  return (
    <RadixCallout.Root
      {...props}
      size={size}
      color={COLOR_MAP[status]}
      role={role}
      className={clsx(
        'flex! border py-3.5',
        {
          'border-sky-a6': status === 'info',
          'border-success-a6': status === 'success',
          'border-warning-a6': status === 'warning',
          'border-error-a6': status === 'error',
          'bg-sky-3!': status === 'info',
          'bg-success-3!': status === 'success',
          'bg-warning-3!': status === 'warning',
          'bg-error-3!': status === 'error',
        },
        className,
      )}
      data-test-id="callout"
    >
      <RadixCallout.Icon>
        <CalloutIcon
          status={status}
          className={clsx({
            'size-4': size === '1' || size === '2',
            'size-6': size === '3',
          })}
        />
      </RadixCallout.Icon>

      <div className="rt-Text rt-r-size-2 rt-CalloutText flex-1 min-w-0 wrap-break-word">{children}</div>

      {onClose && (
        <IconButton type="button" size="1" variant="ghost" className={clsx('mt-0! text-neutral-12!')} onClick={onClose}>
          <CloseIcon
            className={clsx({
              'size-6': size === '3',
            })}
          />
        </IconButton>
      )}

      {customCloseButton}
    </RadixCallout.Root>
  );
};
